import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Step, StepLabel, Stepper } from '@mui/material';
import { useFormik } from 'formik';
import {
    CloseConfirmationDialog, LoadingBackDrop, Message, MessageDialog, PrimarySaveConfirmationDialog, StepperDialog
} from '@lp/lp-ui';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { userDataReset, userDataSelected } from '../../app/userSlice';
import identityService from '../../services/IdentityService';
import { getBodyErrors } from '../../services/ApiService';
import { EmailSchema } from '../../validationSchemes';
import { NewUserDataModel } from '../../functions';
import { InvitationResponseModel, InviteEmailCheckOutcome } from '../../typings';
import { toErrorMessages, toInviteUserRequestModel } from './invitationHelpers';
import InviteeEmailPanel, { InviteeModel } from './InviteeEmailPanel';
import NewUserApplicationsPanel from './NewUserApplicationsPanel';
import NewUserRolesPanel from './NewUserRolesPanel';
import NewUserOrganizationsPanel from './NewUserOrganizationsPanel';
import InvitationResultPanel from './InvitationResultPanel';

interface InviteUserDialogProps {
    open: boolean,
    handleClose: () => void
}

export const FOREIGN_INVITEE_MESSAGE = 'This person holds an expired invitation in an organization you cannot administer.';
export const EMAIL_TAKEN_MESSAGE = 'Email is already in use.';
export const UNAVAILABLE_MESSAGE = 'This address cannot be invited right now.';

const steps = ['Invitee', 'Applications', 'Roles', 'Organizations', 'Result'];
const resultStep = steps.length - 1;

// Its own component rather than a mode of AddUserDialog: that wizard validates by step index,
// which makes a conditional step list fragile. The assignment panels are shared as-is.
const InviteUserDialog: React.FC<InviteUserDialogProps> = ({ open, handleClose }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { data } = useAppSelector(s => s.user);
    const [activeStep, setActiveStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const [expiredInviteeUserId, setExpiredInviteeUserId] = useState<number | null>(null);
    // An expired invitee skips the assignment steps; the stepper must not show them as passed.
    const [shortCircuited, setShortCircuited] = useState(false);
    // The link is a bearer credential and the user slice is persisted to session storage.
    // It lives here and nowhere else.
    const [result, setResult] = useState<InvitationResponseModel | null>(null);

    const isResultStep = activeStep === resultStep;
    const visibleSteps = shortCircuited ? [steps[0], steps[resultStep]] : steps;
    const stepperIndex = shortCircuited && isResultStep ? 1 : activeStep;

    const formik = useFormik<InviteeModel>({
        initialValues: { email: '', twoFactorEnabled: false },
        validationSchema: EmailSchema,
        onSubmit: async (values, helpers) => {
            setBusy(true);
            try {
                const check = await identityService.checkInviteEmail(values.email);
                switch (check.data.outcome) {
                    case InviteEmailCheckOutcome.Available:
                        // Back → same address → Next keeps the assignments already chosen; a new address starts clean.
                        dispatch(userDataSelected(
                            data !== null && data.email === values.email
                                ? { ...data, twoFactorEnabled: values.twoFactorEnabled }
                                : { ...NewUserDataModel(), userAddresses: [], email: values.email, twoFactorEnabled: values.twoFactorEnabled }
                        ));
                        // A failed resend leaves the flag set; starting a normal run must clear it.
                        setShortCircuited(false);
                        setActiveStep(1);
                        break;
                    case InviteEmailCheckOutcome.ExpiredInvitee:
                        // The API omits a null userId, so test for the number, not for null.
                        if (typeof check.data.userId === 'number') {
                            setExpiredInviteeUserId(check.data.userId);
                        } else {
                            helpers.setFieldError('email', UNAVAILABLE_MESSAGE);
                        }
                        break;
                    case InviteEmailCheckOutcome.ExpiredInviteeForeign:
                        helpers.setFieldError('email', FOREIGN_INVITEE_MESSAGE);
                        break;
                    case InviteEmailCheckOutcome.Taken:
                        helpers.setFieldError('email', EMAIL_TAKEN_MESSAGE);
                        break;
                    default:
                        helpers.setFieldError('email', UNAVAILABLE_MESSAGE);
                }
            } catch (err) {
                setMessages(toErrorMessages(await getBodyErrors(err)));
            } finally {
                setBusy(false);
            }
        }
    });

    const showResult = (response: InvitationResponseModel) => {
        setResult(response);
        setActiveStep(resultStep);
    };

    const handleNext = async () => {
        if (activeStep === 0) {
            await formik.submitForm();
        } else {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const handleSend = async () => {
        if (data === null) return;
        setBusy(true);
        try {
            const response = await identityService.inviteUser(toInviteUserRequestModel(data));
            showResult(response.data);
        } catch (err) {
            setMessages(toErrorMessages(await getBodyErrors(err)));
        } finally {
            setBusy(false);
        }
    };

    const handleResend = async (userId: number) => {
        setBusy(true);
        try {
            const response = await identityService.resendInvitation(userId);
            showResult(response.data);
        } catch (err) {
            setMessages(toErrorMessages(await getBodyErrors(err)));
        } finally {
            setBusy(false);
        }
    };

    const handleExpiredInviteeConfirm = async () => {
        const userId = expiredInviteeUserId;
        setExpiredInviteeUserId(null);
        if (userId !== null) {
            setShortCircuited(true);
            await handleResend(userId);
        }
    };

    const reset = () => {
        formik.resetForm();
        setActiveStep(0);
        setResult(null);
        setExpiredInviteeUserId(null);
        setShortCircuited(false);
        setCloseDialogOpen(false);
        dispatch(userDataReset());
    };

    const handleFinish = () => {
        const userId = result?.userId;
        reset();
        handleClose();
        if (userId) {
            navigate(`/users/${userId}`);
        }
    };

    const handleCloseOk = () => {
        reset();
        handleClose();
    };

    // On the Result step the invitation already exists — closing behaves like Done.
    const handleCloseClicked = () => {
        if (isResultStep) {
            handleFinish();
            return;
        }
        if (data !== null || formik.dirty) {
            setCloseDialogOpen(true);
        } else {
            handleCloseOk();
        }
    };

    return (
        <React.Fragment>
            <StepperDialog
                handleCancel={handleCloseClicked}
                handleSave={isResultStep ? handleFinish : handleSend}
                handleNext={handleNext}
                handleBack={handleBack}
                handleCloseClicked={handleCloseClicked}
                title="Invite User"
                open={open}
                hasPrevious={activeStep === 0 || isResultStep}
                canSave={activeStep === resultStep - 1 || isResultStep}
                saveLabel={isResultStep ? 'Done' : 'Send Invitation'}
                stepper={<Stepper activeStep={stepperIndex}>
                    {visibleSteps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>}
            >
                <Box minWidth={600}>
                    {activeStep === 0 && <InviteeEmailPanel formik={formik} />}
                    {activeStep === 1 && <NewUserApplicationsPanel />}
                    {activeStep === 2 && <NewUserRolesPanel />}
                    {activeStep === 3 && <NewUserOrganizationsPanel />}
                    {isResultStep && result &&
                        <InvitationResultPanel
                            result={result}
                            resending={busy}
                            onResend={() => handleResend(result.userId)}
                        />
                    }
                    <LoadingBackDrop open={busy} />
                </Box>
            </StepperDialog>

            <PrimarySaveConfirmationDialog
                open={expiredInviteeUserId !== null}
                title="Resend Invitation"
                contentText={`${formik.values.email} already holds an expired invitation. Resend issues a new link with the access granted before; change access from the user's profile. The earlier link will no longer work.`}
                actionButtonText="Resend"
                handleSave={handleExpiredInviteeConfirm}
                handleClose={() => setExpiredInviteeUserId(null)}
            />

            <CloseConfirmationDialog
                open={closeDialogOpen}
                handleCancel={() => setCloseDialogOpen(false)}
                handleConfirm={handleCloseOk} />

            <MessageDialog
                title="Attention"
                messages={messages}
                handleClose={() => setMessages([])}
            />
        </React.Fragment>
    );
};

export default InviteUserDialog;
